import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TerminalService, Terminal, TerminalStatus, ReceiptData } from '../../services/terminal.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { Subscription } from 'rxjs';
import { THEME_COLORS } from '../../constants/theme-colors';

@Component({
	selector: 'app-terminal-transaction-modal',
	templateUrl: './terminal-transaction-modal.component.html',
	styleUrls: ['./terminal-transaction-modal.component.scss']
})
export class TerminalTransactionModalComponent implements OnInit, OnDestroy {
  @Input() terminal: Terminal;
  @Input() amount: number;
  @Input() orderData: any;
  @Input() transactionType: 'sale' | 'refund' | 'void' | 'pre_auth' | 'completion' = 'sale';
  @Input() invoice: string;
  @Input() clerkId: string;

  status: TerminalStatus | null = null;
  transactionId: string | null = null;
  errorMessage: string | null = null;
  statusSubscription: Subscription;
  receiptData: ReceiptData | null = null;
  themeStyle: { [key: string]: string } = {};
  manualAcceptAvailable = false;
  lastTerminalError: string | null = null;
  private pollInterval: any;
  private waitingForCardTimer: any;

  statusMessages = {
  	'pending': 'Waiting on the terminal…',
  	'processing': 'Processing payment...',
  	'waiting_for_card': 'Please insert, swipe, or tap your card',
  	'approved': 'Transaction approved',
  	'partial_approved': 'Transaction partially approved',
  	'declined': 'Transaction declined',
  	'cancelled': 'Transaction cancelled',
  	'error': 'An error occurred'
  };

  entryMethodIcons = {
  	'chip': 'credit_card',
  	'swipe': 'swipe',
  	'contactless': 'contactless',
  	'manual': 'keyboard',
  	'chip_fallback_swipe': 'credit_card'
  };

  constructor(
    public activeModal: NgbActiveModal,
    private terminalService: TerminalService,
    private globalSearchService: GlobalSearchService
  ) {}

  ngOnInit() {
  	const color = this.globalSearchService.getColor();
  	const theme = THEME_COLORS[color] || THEME_COLORS['blue'];
  	this.themeStyle = {
  		'--bm-theme': theme.main,
  		'--bm-theme-rgb': theme.rgb
  	};
  	this.initiateTransaction();
  	this.subscribeToStatusUpdates();
  }

  ngOnDestroy() {
  	if (this.statusSubscription) {
  		this.statusSubscription.unsubscribe();
  	}
  	if (this.pollInterval) {
  		clearInterval(this.pollInterval);
  	}
  	if (this.waitingForCardTimer) {
  		clearTimeout(this.waitingForCardTimer);
  	}
  }

  initiateTransaction() {
  	if (!this.terminal.terminal_id) {
  		this.errorMessage = 'Invalid terminal selected';
  		return;
  	}

  	this.terminalService.initiateTransaction({
  		terminal_id: this.terminal.terminal_id,
  		terminal_serial: this.terminal.terminal_serial,
  		terminal_name: this.terminal.terminal_name,
  		amount: this.amount,
  		transaction_type: this.transactionType,
  		order_data: this.orderData,
  		invoice: this.invoice,
  		clerk_id: this.clerkId
  	}).subscribe(
  		(response: any) => {
  			this.transactionId = (response.transaction_id || response.terminal_transaction_id)?.toString();
  			this.status = {
  				transaction_id: this.transactionId,
  				status: 'pending',
  				message: 'Transaction initiated',
  				timestamp: new Date().toISOString()
  			};
  			if (this.waitingForCardTimer) {
  				clearTimeout(this.waitingForCardTimer);
  			}
  		},
  		(error) => {
				this.status = null;
				this.receiptData = null;
  			if (this.waitingForCardTimer) {
  				clearTimeout(this.waitingForCardTimer);
  			}
  			this.errorMessage = error?.error?.message || 'Failed to initiate transaction. Please try again.';
  			this.lastTerminalError = this.errorMessage;
  			this.manualAcceptAvailable = this.isRecoverableTerminalError(this.errorMessage);
  			console.error('Transaction initiation error:', error);
  		}
  	);
  }

  subscribeToStatusUpdates() {
  	this.statusSubscription = this.terminalService.status$.subscribe(
  		(status: TerminalStatus) => {
  			if (status.transaction_id === this.transactionId) {
  				this.status = status;
  				if (this.waitingForCardTimer) {
  					clearTimeout(this.waitingForCardTimer);
  				}
  				if (status.receipt_data) {
  					this.receiptData = status.receipt_data;
  				}
  				if (status.status === 'approved' || status.status === 'partial_approved') {
  					setTimeout(() => {
  						this.activeModal.close({
  							success: true,
  							status: status.status,
  							terminal_transaction_id: status.transaction_id,
  							terminal_id: status.terminal_id || this.terminal?.terminal_id,
  							transaction_type: this.transactionType,
  							auth_code: status.auth_code,
  							card_last_four: status.card_last_four,
  							card_type: status.card_type,
  							card_type_code: status.card_type_code,
  							entry_method: status.entry_method,
  							entry_mode_code: status.entry_mode_code,
  							amount: status.amount || this.amount,
  							tip_amount: status.tip_amount,
  							total_amount: status.total_amount,
  							invoice: status.invoice,
  							reference: status.reference,
  							transaction_sequence: status.transaction_sequence,
  							response_code: status.response_code,
  							response_text: status.response_text,
  							receipt_data: status.receipt_data,
  							emv_aid: status.emv_aid,
  							emv_app_label: status.emv_app_label,
  							partial_approved: status.status === 'partial_approved',
  							amount_due: status.amount_due,
  							client_sent_at: status.client_sent_at,
  							node_received_at: status.node_received_at,
  							print_connect_received_at: status.print_connect_received_at,
  							print_connect_responded_at: status.print_connect_responded_at,
  							node_broadcast_at: status.node_broadcast_at,
  							ecr_print_success: status.ecr_print_success !== false,
  							ecr_print_error: status.ecr_print_error,
  							early_approval: status.early_approval === true
  						});
  					}, 2000);
  				} else if (status.status === 'declined' || status.status === 'error') {
  					this.errorMessage = status.message || status.response_text || 'Transaction failed';
  					this.lastTerminalError = this.errorMessage;
  					this.manualAcceptAvailable = status.manual_accept_recommended === true || this.isRecoverableTerminalError(this.errorMessage);
  				}
  			}
  		}
  	);
  }

  isRecoverableTerminalError(message: string | null | undefined): boolean {
  	const text = (message || '').toLowerCase();
  	return text.includes('connection closed before transaction completed') ||
  		text.includes('connection closed during ecr') ||
  		text.includes('connection closed while sending ecr') ||
  		text.includes('terminal disconnected');
  }

  getEntryMethodIcon(method: string): string {
  	return this.entryMethodIcons[method] || 'payment';
  }

  getStatusMessage(): string {
  	if (!this.status) return 'Initializing...';
  	return this.statusMessages[this.status.status] || this.status.message || 'Processing...';
  }

  getHeaderStatusText(): string {
  	if (this.errorMessage && !this.status) return this.errorMessage;
  	return this.getStatusMessage();
  }

  isTransactionInProgress(): boolean {
  	if (this.errorMessage && !this.status) return false;
  	if (!this.status) return true;
  	const s = this.status.status;
  	return s === 'pending' || s === 'waiting_for_card' || s === 'processing';
  }

  cancelTransaction() {
  	if (this.transactionId) {
  		this.terminalService.voidTransaction(this.transactionId).subscribe(
  			() => {
  				this.activeModal.dismiss('cancelled');
  			},
  			(error) => {
  				console.error('Void error:', error);
  				this.activeModal.dismiss('cancelled');
  			}
  		);
  	} else {
  		this.activeModal.dismiss('cancelled');
  	}
  }

  retryTransaction() {
  	this.errorMessage = null;
  	this.status = null;
  	this.transactionId = null;
  	this.manualAcceptAvailable = false;
  	this.lastTerminalError = null;
  	this.initiateTransaction();
  }

  acceptTerminalPayment() {
  	this.activeModal.close({
  		success: true,
  		status: 'approved',
  		manual_terminal_accept: true,
  		terminal_transaction_id: this.transactionId || ('manual_' + Date.now()),
  		terminal_id: this.terminal?.terminal_id,
  		transaction_type: this.transactionType,
  		amount: this.amount,
  		total_amount: this.amount,
  		response_text: this.lastTerminalError || this.errorMessage || 'Terminal payment manually accepted after connection error',
  		ecr_print_success: this.status?.ecr_print_success !== false,
  		ecr_print_error: this.status?.ecr_print_error
  	});
  }
}
